import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { addCommasToNumber, GraviButton, GraviGrid, onExport, Texto } from '@gravitate-js/excalibrr'
import { useAnalyticsReporting } from '@api/useAnalyticsReporting'
import { useUserSettings } from '@hooks/settings'
import moment from 'moment/moment'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { defaultDateDef, defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { useModelConfig } from '@hooks/useModelConfig'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { formatters } from '@utils/intl/dateTimeFormatters'
import queryString from 'query-string'
import { useEvents } from '@api/useEvents'
import { DateRangeFilter } from '@/components/_SiteWide/DateRangeFilter'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'analytics-reporting-sales'

export const Sales = () => {
  const { useSalesOverview } = useAnalyticsReporting()
  const { updateSetting } = useUserSettings()
  const gridAPIRef = useRef(null)
  const { config } = useModelConfig()
  const { affectedMetadataQuery } = useEvents()
  const [eventId, setEventId] = useState(queryString.parse(window.location.search).event_id)
  const [event, setEvent] = useState(null)
  const {
    data: affectedEventsMetadata,
    isPending: isEventsMetadataLoading,
    isError: isEventsMetadataError,
  } = affectedMetadataQuery(eventId)

  const { value: dayRange, setValue: setLocalDayRange } = useLocalStorage('analytics-reporting-sales-dates', {
    back: -30,
    ahead: -1,
  })
  const setDayRange = async (range) => {
    setLocalDayRange(range)
    await updateSetting('analytics-reporting-sales-dates', range)
  }

  const [filters, setFilters] = useState({
    To: moment()
      .add(dayRange.ahead ?? -1, 'days')
      .toDate(),
    From: moment()
      .add(dayRange.back ?? -30, 'days')
      .toDate(),
  })
  const { data, status } = useSalesOverview({
    start_date: moment(filters.From).format('YYYY-MM-DDT00:00:00'),
    end_date: moment(filters.To).format('YYYY-MM-DDT00:00:00'),
  })
  const columnDefs = useMemo(() => getColumnDefs(config), [config])
  const getRowId = useCallback((row) => row?.data?.key, [])
  const agPropOverrides = useMemo(() => ({ getRowId }), [getRowId])

  const filteredRowData = useMemo(() => {
    if (eventId) {
      return data?.filter((row) => row.event_id === eventId)
    }
    return data
  }, [eventId, data])

  useEffect(() => {
    setEventId(queryString.parse(window.location.search).event_id)
  }, [])

  const clearEventFilter = () => {
    setEventId(null)
  }

  return (
    <GraviGrid
      loading={status === 'pending'}
      externalRef={gridAPIRef}
      rowData={filteredRowData}
      storageKey={STORAGE_KEY}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      controlBarProps={{
        title: 'Sales',
        actionButtons: (
          <ActionButtons
            gridAPIRef={gridAPIRef}
            filters={filters}
            setFilters={setFilters}
            setDayRange={setDayRange}
            dayRangeStorage={dayRange}
            clearEventFilter={clearEventFilter}
            eventId={!!eventId}
          />
        ),
      }}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

export const ActionButtons = ({
  gridAPIRef,
  filters,
  setFilters,
  setDayRange,
  dayRangeStorage,
  clearEventFilter,
  eventId,
}) => {
  const excelOptions = [
    {
      title: 'Download Sales Report',
      onClick: () =>
        onExport(gridAPIRef, {
          fileName: `SalesAnalyticsReport${moment().format('YYYY-MM-DD')}.xlsx`,
          sheetName: 'Sales Report',
          processCellCallback(params) {
            if (params.value && params.column.colId === 'date') {
              return formatters.DATE(moment(params.value))
            }
            return params.value
          },
        }),
    },
  ]

  return (
    <>
      {eventId && (
        <GraviButton theme1 className='square-ish mr-3' buttonText='Clear Event Filter' onClick={clearEventFilter} />
      )}
      <UploadDownloadButtons excelOptions={excelOptions} />
      <DateRangeFilter
        min={-30}
        max={-1}
        currentFilters={filters}
        setCurrentFilters={setFilters}
        getFromStorage
        dayRangeStorage={dayRangeStorage}
        setDayRangeStorage={setDayRange}
      />
    </>
  )
}

export const getColumnDefs = (config) => [
  { headerName: 'Product', field: 'product' },
  {
    headerName: 'Site',
    field: 'site',
    comparator: (a, b) => a - b,
  },
  { headerName: 'Tank', field: 'tank' },
  { headerName: 'Date', field: 'date', ...defaultDateDef('date', formatters.DATE) },
  { headerName: 'Market', field: 'market' },
  { headerName: 'Sector', field: 'sector' },
  {
    headerName: 'Customer',
    field: 'customer',
    valueGetter: (params) => params?.data?.customer || '',
  },
  {
    headerName: 'Sales',
    field: 'sales',
    ...defaultNumberDef('sales', 0),
    valueGetter: (params) => params?.data?.sales || -Infinity,
    valueFormatter: (params) => (params?.data?.sales ? addCommasToNumber(params.data.sales, 0) : ''),
  },
  {
    headerName: 'Forecast Snapshot',
    headerTooltip: `Forecast is as of ${config?.forecast_snapshot_days_back} days back.`,
    field: 'forecast_snapshot',
    ...defaultNumberDef('forecast_snapshot', 0),
    valueGetter: (params) => params?.data?.forecast_snapshot || -Infinity,
    valueFormatter: (params) =>
      params?.data?.forecast_snapshot ? addCommasToNumber(params.data.forecast_snapshot, 0) : '',
  },
  {
    headerName: 'Forecast Variance',
    field: 'forecast_variance',
    ...defaultNumberDef('forecast_variance', 0),
    valueFormatter: (params) =>
      params?.data?.forecast_variance ? addCommasToNumber(params.data.forecast_variance, 0) : '',
    valueGetter: (params) => params?.data?.forecast_variance || -Infinity,
  },
  {
    headerName: 'Forecast Variance Percentage',
    field: 'forecast_variance',
    valueFormatter: (params) => {
      const { forecast_variance, forecast_snapshot } = params?.data
      if (!forecast_variance || !forecast_snapshot) return ''
      return `${addCommasToNumber((forecast_variance / forecast_snapshot) * 100, 0)}%`
    },
    valueGetter: (params) => {
      const { forecast_variance, forecast_snapshot } = params?.data
      if (!forecast_variance || !forecast_snapshot) return -Infinity
      return (forecast_variance / forecast_snapshot) * 100
    },
    type: 'rightAligned',
    filter: 'agNumberColumnFilter',
  },
  { headerName: 'Source', field: 'source' },
  { headerName: 'Overridden User', field: 'overridden_user' },
  {
    headerName: 'Original Value',
    field: 'original_value',
    ...defaultNumberDef('original_value', 0),
    valueFormatter: (params) =>
      params?.data?.original_value !== null ? addCommasToNumber(params?.data?.original_value, 0) : '',
    valueGetter: (params) => params?.data?.original_value || -Infinity,
  },
  {
    headerName: 'Event Name',
    field: 'event_name',
  },
  {
    headerName: 'Excluded From Forecast',
    field: 'exclude_from_forecast',
    cellRenderer: (params) => {
      return params?.data?.exclude_from_forecast ? 'Yes' : 'No'
    },
    valueGetter: (params) => (params?.data?.exclude_from_forecast ? 'Yes' : 'No'),
    valueFormatter: (params) => (params?.data?.exclude_from_forecast ? 'Yes' : 'No'),
  },
]
